import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Clock, Play } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/api";
import usePullToRefresh from "../hooks/usePullToRefresh";

function StarlineBetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [results, setResults] = useState([]);
  const [rates, setRates] = useState({});
  const [marketId, setMarketId] = useState(id || "");

  const loadStarlineData = useCallback(async () => {
    const [controlRes, resultRes, ratesRes] = await Promise.all([
      API.get("/starline/control"),
      API.get("/starline/results").catch(() => ({ data: [] })),
      API.get("/game-rates?market_type=starline").catch(() => ({ data: {} })),
    ]);
    const marketRows = Array.isArray(controlRes.data?.markets) ? controlRes.data.markets : [];
    const slotRows = Array.isArray(controlRes.data?.slots)
      ? controlRes.data.slots.filter((slot) => Number(slot.is_active) === 1)
      : [];
    const selectedMarket = String(id || marketRows[0]?.id || "");
    setMarkets(marketRows);
    setSlots(slotRows);
    setResults(Array.isArray(resultRes.data) ? resultRes.data : []);
    setRates(ratesRes.data || {});
    setMarketId(selectedMarket);
  }, [id]);

  const pullToRefresh = usePullToRefresh(loadStarlineData);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    let mounted = true;
    loadStarlineData().catch(() => {
      if (!mounted) return;
      setMarkets([]); setSlots([]); setResults([]);
    });
    return () => { mounted = false; };
  }, [loadStarlineData]);

  const marketSlots = slots.filter((slot) => String(slot.market_id) === String(marketId));
  const market = markets.find((item) => String(item.id) === String(marketId)) || markets[0];

  const openSlot = (slot) => {
    navigate(`/starline/${marketId}/play?slot=${encodeURIComponent(slot.time_slot)}`);
  };

  const rS = (v, fb) => Number(v || 0) > 0 ? `1-${Number(v).toFixed(0)}` : fb;

  return (
    <div style={page} {...pullToRefresh.bind}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => navigate("/dashboard", { replace: true })}>
          <ArrowLeft size={20} color="#312e81" />
        </button>
        <span style={headerTitle}>{market?.name || "Bharat Starline"}</span>
        <button type="button" style={historyBtn} onClick={() => navigate("/bids?type=starline")}>
          History
        </button>
      </header>

      {/* Rates box */}
      <div style={ratesBox}>
        <div style={ratesCol}>
          <div style={rateLine}>Single Digit: <span style={rateVal}>{rS(rates.single, "1-10")}</span></div>
          <div style={rateLine}>Single Pana: <span style={rateVal}>{rS(rates.sp, "1-160")}</span></div>
        </div>
        <div style={ratesDivider} />
        <div style={ratesCol}>
          <div style={rateLine}>Double Pana: <span style={rateVal}>{rS(rates.dp, "1-320")}</span></div>
          <div style={rateLine}>Triple Pana: <span style={rateVal}>{rS(rates.tp, "1-1000")}</span></div>
        </div>
      </div>

      {/* Market tabs */}
      {markets.length > 1 && (
        <div style={marketTabRow}>
          {markets.map((m) => (
            <button
              key={m.id}
              type="button"
              style={String(m.id) === String(marketId) ? marketTabActive : marketTab}
              onClick={() => setMarketId(String(m.id))}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      {/* Slot list */}
      <main style={main}>
        {marketSlots.map((slot, idx) => {
          const result = resultForSlot(results, marketId, slot.time_slot);
          const playable = isSlotPlayable(slot.time_slot);
          return (
            <div key={slot.id} style={{ ...slotRow, borderTop: idx === 0 ? "none" : "1px solid #e5e7eb" }}>
              {/* Left: time + status */}
              <div style={slotLeft}>
                <div style={slotTime}>{slot.time_slot}</div>
                <div style={playable ? statusOpen : statusClosed}>
                  {playable ? "Betting is Running Now" : "Betting is Closed For Today"}
                </div>
              </div>

              {/* Center: result */}
              <div style={resultBlock}>
                <span style={result ? resultVal : resultPending}>
                  {result ? `${result.pana}-${result.digit}` : "***-*"}
                </span>
              </div>

              {/* Right: play button */}
              <button
                type="button"
                style={playable ? playBtn : playBtnOff}
                disabled={!playable}
                onClick={() => openSlot(slot)}
              >
                <Play size={18} fill="currentColor" />
              </button>
            </div>
          );
        })}

        {marketSlots.length === 0 && (
          <div style={emptyState}>
            <Clock size={32} color="#cbd5e1" />
            <p>No time slots available</p>
          </div>
        )}
      </main>
    </div>
  );
}

/* helpers */
const toLocalDateKey = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  return String(value).slice(0, 10);
};
const todayKey = () => toLocalDateKey(new Date());
const resultForSlot = (results, marketId, timeSlot) => {
  const match = (item) => String(item.market_id) === String(marketId) && String(item.result_hour) === String(timeSlot);
  return results.find((item) => match(item) && toLocalDateKey(item.result_date) === todayKey())
    || results.find((item) => match(item))
    || null;
};
const timeToMinutes = (label = "") => {
  const m = String(label).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return 0;
  let hour = Number(m[1]);
  const min = Number(m[2]);
  const meridian = m[3].toUpperCase();
  if (meridian === "PM" && hour !== 12) hour += 12;
  if (meridian === "AM" && hour === 12) hour = 0;
  return hour * 60 + min;
};
const isSlotPlayable = (timeSlot) => {
  const now = new Date();
  if (now.getHours() === 1) return true;
  return timeToMinutes(timeSlot) >= now.getHours() * 60 + now.getMinutes();
};

/* styles */
const page = {
  minHeight: "100vh",
  background: "#f5f5f5",
  fontFamily: "'Segoe UI', Inter, system-ui, sans-serif",
  color: "#111827",
  paddingBottom: 80,
};

const header = {
  background: "#fff",
  height: 54,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 14px",
  borderBottom: "1px solid #e5e7eb",
  position: "sticky",
  top: 0,
  zIndex: 10,
};
const backBtn = {
  width: 36, height: 36, borderRadius: "50%", border: "1px solid #e0e7ff",
  background: "#f5f3ff", display: "grid", placeItems: "center", cursor: "pointer",
};
const headerTitle = { fontSize: 17, fontWeight: 900, color: "#1e1b4b", letterSpacing: "0.01em" };
const historyBtn = {
  fontSize: 13, fontWeight: 700, color: "#4f46e5",
  border: "1px solid #c7d2fe", background: "#eef2ff",
  borderRadius: 999, padding: "5px 14px", cursor: "pointer",
};

const ratesBox = {
  background: "#fff",
  margin: "10px 14px 0",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "stretch",
  overflow: "hidden",
};
const ratesCol = {
  flex: 1, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6,
};
const ratesDivider = { width: 1, background: "#e5e7eb", flexShrink: 0 };
const rateLine = { fontSize: 13, color: "#374151", fontWeight: 500 };
const rateVal = { color: "#2563eb", fontWeight: 800 };

const marketTabRow = {
  display: "flex", gap: 8, padding: "10px 14px", overflowX: "auto",
};
const marketTab = {
  padding: "6px 16px", borderRadius: 999, border: "1px solid #e5e7eb",
  background: "#fff", fontSize: 13, fontWeight: 600, color: "#6b7280",
  whiteSpace: "nowrap", cursor: "pointer", flexShrink: 0,
};
const marketTabActive = {
  ...marketTab, background: "#312e81", border: "1px solid #312e81", color: "#fff",
};

const main = {
  background: "#fff",
  margin: "10px 14px 0",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  overflow: "hidden",
};

const slotRow = {
  display: "flex",
  alignItems: "center",
  padding: "14px 18px",
  gap: 12,
};
const slotLeft = { flex: "0 0 auto", minWidth: 110 };
const slotTime = { fontSize: 15, fontWeight: 800, color: "#111827", marginBottom: 3 };
const statusOpen = { fontSize: 12, fontWeight: 700, color: "#16a34a" };
const statusClosed = { fontSize: 12, fontWeight: 700, color: "#dc2626" };

const resultBlock = { flex: 1, textAlign: "center" };
const resultVal = { fontSize: 20, fontWeight: 900, color: "#312e81", letterSpacing: "0.06em" };
const resultPending = { fontSize: 20, fontWeight: 900, color: "#312e81", letterSpacing: "0.06em" };

const playBtn = {
  width: 44, height: 44, borderRadius: "50%",
  border: "2px solid #312e81", background: "transparent",
  color: "#312e81", display: "grid", placeItems: "center",
  cursor: "pointer", flexShrink: 0,
};
const playBtnOff = {
  ...playBtn, border: "2px solid #d1d5db", color: "#d1d5db", cursor: "default",
};

const emptyState = {
  textAlign: "center", color: "#9ca3af", padding: "40px 0",
  display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
};

export default StarlineBetPage;
